import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Upload, FileText, X, Loader2, PartyPopper } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";
import { submitMerchantSignup, uploadPublicFile } from "../api";
import useBranding from "../hooks/useBranding";

const STEPS = ["Account", "Business", "KYC Details", "Bank", "Documents", "Agreement"];

const DOC_TYPES = [
  { key: "pan_card", label: "PAN Card" },
  { key: "aadhaar_front", label: "Aadhaar (Front)" },
  { key: "aadhaar_back", label: "Aadhaar (Back)" },
  { key: "cancelled_cheque", label: "Cancelled Cheque / Passbook" },
];

const field = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0f2540] outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition";
const labelCls = "block text-sm font-semibold text-[#0D1B2A] mb-1.5";

const Signup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const b = useBranding();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState("");
  const [docs, setDocs] = useState({}); // key -> {file_id,url,filename,label}
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", password: "",
    business_name: "", business_type: "Retailer", gstin: "",
    pan_number: "", aadhaar_number: "", address: "", city: "", state: "", pincode: "",
    bank_account: "", ifsc: "", account_holder: "",
    agreement_accepted: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.full_name || !form.email || !form.phone) return "Please fill name, email and phone.";
      if (!form.password || form.password.length < 6) return "Set a password of at least 6 characters.";
    }
    if (step === 1 && !form.business_name) return "Please enter your business name.";
    if (step === 2) {
      if (!form.pan_number) return "PAN number is required.";
      if (!form.aadhaar_number) return "Aadhaar number is required.";
    }
    if (step === 3 && (!form.bank_account || !form.ifsc)) return "Bank account and IFSC are required.";
    if (step === 5 && !form.agreement_accepted) return "Please accept the agreement to continue.";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast({ title: err }); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onUpload = async (docType, fileObj, label) => {
    if (!fileObj) return;
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await uploadPublicFile(fd);
      setDocs((d) => ({ ...d, [docType]: { label, file_id: res.id, url: res.url, filename: res.filename } }));
      toast({ title: `${label} uploaded` });
    } catch {
      toast({ title: "Upload failed", description: "Please try a smaller file (max 8MB)." });
    } finally {
      setUploading("");
    }
  };

  const submit = async () => {
    const err = validateStep();
    if (err) { toast({ title: err }); return; }
    setSubmitting(true);
    try {
      await submitMerchantSignup({ ...form, documents: Object.values(docs) });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast({ title: "Submission failed", description: "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-[150px] pb-32 pp-container max-w-xl text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <PartyPopper size={38} className="text-green-600" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-[#0D1B2A]">Application Submitted!</h1>
          <p className="mt-3 text-[#475569]">Thank you for signing up with {b.company_name}. Our onboarding team will review your KYC and activate your merchant account shortly.</p>
          <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 bg-[#0D1B2A] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#06B6D4] transition-colors">
            Back to Home <ArrowRight size={16} />
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f9fd]">
      <Header />
      <section className="pt-[130px] pb-20">
        <div className="pp-container max-w-4xl">
          <div className="text-center mb-10">
            <span className="text-[#06B6D4] font-semibold uppercase tracking-wider text-sm">Merchant Onboarding</span>
            <h1 className="mt-2 text-3xl md:text-4xl font-extrabold text-[#0D1B2A]">Become a {b.company_name} Partner</h1>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-between mb-10 overflow-x-auto">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center shrink-0">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    i < step ? "bg-green-500 text-white" : i === step ? "bg-[#06B6D4] text-white" : "bg-white text-[#64748B] border border-slate-200"
                  }`}>
                    {i < step ? <Check size={16} /> : i + 1}
                  </div>
                  <span className={`mt-2 text-[11px] font-semibold ${i === step ? "text-[#0D1B2A]" : "text-[#64748B]"}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`w-8 md:w-16 h-0.5 mx-1 md:mx-2 ${i < step ? "bg-green-500" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl p-7 md:p-10 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.35)] border border-slate-100">
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">Create your account</h2>
                <div><label className={labelCls}>Full Name *</label><input className={field} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Email *</label><input className={field} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                  <div><label className={labelCls}>Phone *</label><input className={field} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                </div>
                <div><label className={labelCls}>Password *</label><input className={field} type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" /></div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">Business details</h2>
                <div><label className={labelCls}>Business Name *</label><input className={field} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Business Type</label>
                    <select className={field} value={form.business_type} onChange={(e) => set("business_type", e.target.value)}>
                      {["Retailer", "Distributor", "Proprietorship", "Partnership", "Private Limited", "Other"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>GSTIN (optional)</label><input className={field} value={form.gstin} onChange={(e) => set("gstin", e.target.value)} /></div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">KYC details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>PAN Number *</label><input className={field} value={form.pan_number} onChange={(e) => set("pan_number", e.target.value.toUpperCase())} placeholder="ABCDE1234F" /></div>
                  <div><label className={labelCls}>Aadhaar Number *</label><input className={field} value={form.aadhaar_number} onChange={(e) => set("aadhaar_number", e.target.value)} placeholder="XXXX XXXX XXXX" /></div>
                </div>
                <div><label className={labelCls}>Address</label><input className={field} value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><label className={labelCls}>City</label><input className={field} value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                  <div><label className={labelCls}>State</label><input className={field} value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                  <div><label className={labelCls}>Pincode</label><input className={field} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} /></div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">Bank account</h2>
                <div><label className={labelCls}>Account Holder Name</label><input className={field} value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Account Number *</label><input className={field} value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></div>
                  <div><label className={labelCls}>IFSC Code *</label><input className={field} value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} /></div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">Upload documents</h2>
                <p className="text-sm text-[#475569]">Upload clear images or PDFs (max 8MB each).</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {DOC_TYPES.map((d) => {
                    const uploaded = docs[d.key];
                    return (
                      <div key={d.key} className={`rounded-2xl border-2 border-dashed p-5 transition-colors ${uploaded ? "border-green-400 bg-green-50" : "border-slate-200 hover:border-[#0EA5E9]"}`}>
                        <label className="cursor-pointer block text-center">
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onUpload(d.key, e.target.files[0], d.label)} />
                          {uploading === d.key ? (
                            <Loader2 size={26} className="mx-auto text-[#0EA5E9] animate-spin" />
                          ) : uploaded ? (
                            <FileText size={26} className="mx-auto text-green-600" />
                          ) : (
                            <Upload size={26} className="mx-auto text-[#0EA5E9]" />
                          )}
                          <div className="mt-2 font-semibold text-sm text-[#0D1B2A]">{d.label}</div>
                          <div className="text-xs text-[#64748B] mt-1 truncate">{uploaded ? uploaded.filename : "Click to upload"}</div>
                        </label>
                        {uploaded && (
                          <button onClick={() => setDocs((x) => { const c = { ...x }; delete c[d.key]; return c; })} className="mt-2 mx-auto flex items-center gap-1 text-xs text-red-500">
                            <X size={12} /> Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-[#0D1B2A]">Review & agreement</h2>
                <div className="rounded-2xl bg-[#f6f9fd] p-5 text-sm text-[#475569] max-h-48 overflow-y-auto leading-relaxed">
                  By submitting this application you confirm that the information and documents provided are accurate and belong to you. You authorise {b.company_name} to verify your KYC details with authorised sources for merchant onboarding, in line with RBI guidelines and {b.company_name}'s terms of service and privacy policy.
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-1 w-4 h-4 accent-[#06B6D4]" checked={form.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} />
                  <span className="text-sm text-[#1E293B]">I have read and accept the eAgreement, Terms of Service and Privacy Policy.</span>
                </label>
              </div>
            )}

            {/* Nav buttons */}
            <div className="mt-9 flex items-center justify-between">
              <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 text-[#0D1B2A] font-semibold disabled:opacity-30">
                <ArrowLeft size={16} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button onClick={next} className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3 rounded-full transition-transform hover:scale-[1.03]" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3 rounded-full transition-transform hover:scale-[1.03] disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
                  {submitting ? "Submitting…" : "Submit Application"} <Check size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Signup;
